import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { CashbackLevelType } from '../types';

import { BadgeProps, BadgeState } from 'app/design-system/components/Badge';
import { ProgressBarProps, ProgressBarState } from 'app/design-system/components/ProgressBar';
import { useTranslation } from 'app/lang';
import { selectCashback } from 'app/lib/account/selectors';
import { getTruncatedNumber } from 'app/lib/formatters';
import { selectCashbackLevels } from 'app/lib/site-configs/selectors';

import dictionary from '../CashbackLevel.dictionary.json';

type LevelConfig = {
  title: string;
  description: string;
  percentRate: number;
  progressBarProps: ProgressBarProps;
  badgeProps: BadgeProps;
};

const truncatedNumberOptions = {
  upperCase: true,
  startValue: 99999,
  formatOptions: {
    decimals: 1,
  },
};

const formatNumber = ((value: number) => {
  return getTruncatedNumber(value, truncatedNumberOptions);
});

export const useLevelConfig = (levelId: number): LevelConfig => {
  const { t, lang } = useTranslation();
  const cashbackLevels = useSelector(selectCashbackLevels);
  const level = cashbackLevels?.find((level) => levelId === level.levelId);
  const currentLevel = useSelector(selectCashback);
  const currentLevelId = currentLevel?.levelId;
  const nextLevel = cashbackLevels?.find((level) => levelId + 1 === level.levelId);

  const type = useMemo<CashbackLevelType>(() => {
    if (!currentLevelId) {
      return CashbackLevelType.NEXT;
    }

    switch (true) {
      case levelId === currentLevelId:
        return CashbackLevelType.CURRENT;
      case levelId < currentLevelId:
        return CashbackLevelType.PREVIOUS;
      default:
        return CashbackLevelType.NEXT;
    }
  }, [currentLevelId]);

  if (!level) {
    throw new Error(`Can't find cashback level with id ${levelId} in site config cashback levels`);
  }

  if (!currentLevel) {
    throw new Error('Got undefined cashback level from "selectCashback" function');
  }

  const config = useMemo(() => {
    switch (type) {
      case CashbackLevelType.PREVIOUS:
        return {
          title: t(dictionary.previousLevel),
          description: t(dictionary.gainedExperience, {
            experience: formatNumber(level.endExperience),
          }),
          percentRate: level.percentRate,
          badgeProps: {
            state: BadgeState.NOTE,
          },
          progressBarProps: {
            value: 1,
            max: 1,
            state: ProgressBarState.NOTE,
          },
        };
      case CashbackLevelType.CURRENT:
        const maxExperience = currentLevel.endLevelExperience
          ? currentLevel.endLevelExperience - currentLevel.startLevelExperience
          : undefined;

        return {
          title: t(dictionary.currentLevel),
          description: nextLevel
            ? t(dictionary.requiredExperience, {
              percentRate: nextLevel.percentRate,
              experience: formatNumber(level.endExperience - currentLevel.currentExperience),
            })
            : t(dictionary.lastLevelReached),
          percentRate: level.percentRate,
          badgeProps: {
            state: BadgeState.SUCCESS,
          },
          progressBarProps: {
            value: currentLevel.currentExperience - currentLevel.startLevelExperience,
            max: maxExperience,
            state: ProgressBarState.SUCCESS,
          },
        };
      case CashbackLevelType.NEXT:
        return {
          title: t(dictionary.nextLevel),
          description: nextLevel ? t(dictionary.requiredExperience, {
            percentRate: nextLevel.percentRate,
            experience: formatNumber(level.endExperience - currentLevel.currentExperience),
          }) : t(dictionary.lastLevel),
          percentRate: level.percentRate,
          badgeProps: {
            state: BadgeState.NEUTRAL,
          },
          progressBarProps: {
            value: 0,
            max: 1,
            state: ProgressBarState.NEUTRAL,
          },
        };
    }
  }, [type, lang]);

  return config;
};
